import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { $fetch } from 'utils/fetch'

export function useBlockedNumbers(inboundNumber, options = {}) {
  const {
    showActiveOnly = true,
    onToast = () => {},
  } = options

  const queryClient = useQueryClient()

  // Fetch blocked numbers from server
  const { data: blocks = [], isLoading, refetch } = useQuery({
    queryKey: ['blockedNumbers', inboundNumber, showActiveOnly],
    queryFn: async () => {
      const params = new URLSearchParams({
        active_only: showActiveOnly.toString(),
      })
      if (inboundNumber && inboundNumber !== '+10000000000') {
        params.append('inbound_number', inboundNumber)
      }

      const response = await $fetch(`v1/blocked_numbers?${params}`)
      return response.data?.blocks || []
    },
    enabled: !!inboundNumber,
  })

  // API Mutations
  const addBlockMutation = useMutation({
    mutationFn: async (blockData) => {
      const response = await $fetch('v1/blocked_numbers', {
        method: 'POST',
        body: blockData,
      })
      return response.data
    },
    onSuccess: (data) => {
      const count = data.created_count || 1
      onToast({
        title: `${count} block rule${count > 1 ? 's' : ''} added successfully`,
        color: 'success',
      })

      // Update the cache with the new blocks
      const queryKey = ['blockedNumbers', inboundNumber, showActiveOnly]
      queryClient.setQueryData(queryKey, (oldData = []) => [
        ...data.blocks,
        ...oldData,
      ])
    },
    onError: (err) => {
      onToast({
        title: 'Failed to add block rule(s)',
        description: err.message || 'Unknown error',
        color: 'error',
      })
    },
  })

  const editBlockMutation = useMutation({
    mutationFn: async ({ blockId, updates }) => {
      const response = await $fetch(`v1/blocked_numbers/${blockId}/edit`, {
        method: 'POST',
        body: updates,
      })
      return response.data
    },
    onSuccess: (updatedBlock) => {
      onToast({
        title: 'Block rule updated successfully',
        color: 'success',
      })

      // Update the cache with the edited block
      const queryKey = ['blockedNumbers', inboundNumber, showActiveOnly]
      queryClient.setQueryData(queryKey, (oldData = []) =>
        oldData.map(block =>
          block.id === updatedBlock.id ? updatedBlock : block,
        ))
    },
    onError: (err) => {
      onToast({
        title: 'Failed to update block rule',
        description: err.message || 'Unknown error',
        color: 'error',
      })
    },
  })

  const deleteBlockMutation = useMutation({
    mutationFn: async (blockId) => {
      const response = await $fetch(`v1/blocked_numbers/${blockId}`, {
        method: 'DELETE',
      })
      return response.data
    },
    onSuccess: (data) => {
      onToast({
        title: 'Block rule deleted successfully',
        color: 'success',
      })

      // Remove the deleted block from the cache
      const queryKey = ['blockedNumbers', inboundNumber, showActiveOnly]
      queryClient.setQueryData(queryKey, (oldData = []) =>
        oldData.filter(block => block.id !== data.deleted_block_id))
    },
    onError: (err) => {
      onToast({
        title: 'Failed to delete block rule',
        description: err.message || 'Unknown error',
        color: 'error',
      })
    },
  })

  const isMutating = (
    addBlockMutation.isLoading
    || editBlockMutation.isLoading
    || deleteBlockMutation.isLoading
  )

  return {
    blocks,
    isLoading: isLoading || isMutating,
    refetch,
    addBlock: addBlockMutation.mutate,
    editBlock: editBlockMutation.mutate,
    deleteBlock: deleteBlockMutation.mutate,
  }
}
